import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Loader2, Send } from "lucide-react";

import { API } from "@/lib/api";

const INITIAL = { name: "", email: "", phone: "", company: "", subject: "", message: "" };

const inputCls =
  "w-full rounded-xl border border-gray-200 bg-white px-4 py-3 text-sm text-ink outline-none transition-all duration-300 placeholder:text-gray-400 focus:border-navy focus:ring-4 focus:ring-navy/10";

const Field = ({ label, name, type = "text", value, onChange, required, placeholder, textarea }) => (
  <div>
    <label htmlFor={`field-${name}`} className="mb-2 block text-xs font-semibold uppercase tracking-wider text-ink">
      {label} {required && <span className="text-gold">*</span>}
    </label>
    {textarea ? (
      <textarea
        id={`field-${name}`}
        name={name}
        rows={5}
        required={required}
        value={value}
        onChange={onChange}
        placeholder={placeholder}
        data-testid={`contact-${name}-input`}
        className={`${inputCls} resize-none`}
      />
    ) : (
      <input
        id={`field-${name}`}
        name={name}
        type={type}
        required={required}
        value={value}
        onChange={onChange}
        placeholder={placeholder}
        data-testid={`contact-${name}-input`}
        className={inputCls}
      />
    )}
  </div>
);

const ContactForm = ({ defaultSubject = "" }) => {
  const [form, setForm] = useState({ ...INITIAL, subject: defaultSubject });
  const [sending, setSending] = useState(false);

  const onChange = (e) => setForm((f) => ({ ...f, [e.target.name]: e.target.value }));

  const onSubmit = async (e) => {
    e.preventDefault();
    setSending(true);
    try {
      await axios.post(`${API}/contact`, form);
      toast.success("Message sent successfully. Our team will get back to you soon.");
      setForm({ ...INITIAL, subject: defaultSubject });
    } catch (err) {
      toast.error("Could not send your message. Please try again.");
    } finally {
      setSending(false);
    }
  };

  return (
    <form
      onSubmit={onSubmit}
      data-testid="contact-form"
      className="rounded-3xl border border-gray-100 bg-white p-8 shadow-xl shadow-navy/5 sm:p-10"
    >
      <div className="grid gap-6 sm:grid-cols-2">
        <Field label="Name" name="name" value={form.name} onChange={onChange} required placeholder="Your full name" />
        <Field label="Email" name="email" type="email" value={form.email} onChange={onChange} required placeholder="you@company.com" />
        <Field label="Phone" name="phone" type="tel" value={form.phone} onChange={onChange} placeholder="+91 …" />
        <Field label="Company" name="company" value={form.company} onChange={onChange} placeholder="Your company (optional)" />
      </div>
      <div className="mt-6">
        <Field label="Subject" name="subject" value={form.subject} onChange={onChange} required placeholder="How can we help?" />
      </div>
      <div className="mt-6">
        <Field label="Message" name="message" value={form.message} onChange={onChange} required textarea placeholder="Tell us about your requirement…" />
      </div>
      <button
        type="submit"
        disabled={sending}
        data-testid="contact-submit-button"
        className="group mt-8 inline-flex w-full items-center justify-center gap-2 rounded-full bg-navy px-8 py-4 font-heading text-sm font-bold uppercase tracking-widest text-white transition-all duration-300 hover:bg-navy-800 hover:shadow-lg hover:shadow-navy/25 disabled:opacity-60 sm:w-auto"
      >
        {sending ? (
          <>
            <Loader2 className="h-4 w-4 animate-spin" /> Sending…
          </>
        ) : (
          <>
            Send Message
            <Send className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-1" />
          </>
        )}
      </button>
    </form>
  );
};

export default ContactForm;
